import React, { FC, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ui } from 'util/ui';
import { CrossIcon } from './shell/Icons';
import { FOOTER_HEIGHT } from './shell/AppFooter';

/**
 * The small message that slides up bottom left ("Saved", "Copied", …). UI refresh styling
 * (Aaron, 5 Nov 2026): dark rounded toast with the same close cross as the rest of the app,
 * sitting just above the floating footer.
 */

type Props = {
  tooltip: string;
  toggled: boolean;
};

const LowerTooltip: FC<Props> = ({ tooltip, toggled }) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [visible, setVisible] = useState(false);
  const [timeoutID, setTimeoutID] = useState<number>();

  useEffect(() => {
    if (!isInitialLoad) {
      if (tooltip) {
        if (timeoutID) {
          clearTimeout(timeoutID);
        }
        setVisible(true);
        setTimeoutID(
          setTimeout(() => {
            setVisible(false);
          }, 3000),
        );
      }
    } else {
      setIsInitialLoad(false);
    }
  }, [tooltip, toggled]);

  const closeTooltip = () => {
    setVisible(false);
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: 16,
        bottom: FOOTER_HEIGHT + 12,
        zIndex: 500,
        pointerEvents: 'none',
        fontFamily: ui.font,
      }}
    >
      <motion.div
        animate={visible ? 'enter' : 'exit'}
        initial={{ opacity: 0, transform: 'translateY(16px)' }}
        variants={{
          enter: { opacity: 1, transform: 'translateY(0px)' },
          exit: { opacity: 0, transform: 'translateY(16px)' },
        }}
        transition={{ ease: 'easeInOut', duration: 0.25 }}
      >
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: ui.ink,
            color: '#FFFFFF',
            borderRadius: 12,
            boxShadow: '0 12px 30px rgba(0,0,0,0.22)',
            padding: '8px 8px 8px 16px',
            fontSize: 14,
            pointerEvents: visible ? 'auto' : 'none',
            maxWidth: 'calc(100vw - 32px)',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ flex: 1 }}>{tooltip}</span>
          <button
            type="button"
            className="archy-close-dark"
            aria-label="Close message"
            onClick={closeTooltip}
            style={{
              width: 32,
              height: 32,
              flex: 'none',
              border: 'none',
              borderRadius: 8,
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <CrossIcon size={14} color="#FFFFFF" width={2.4} />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default LowerTooltip;
